import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/Button";
import { EmptyState, type EmptyStateAction } from "@/components/ui/EmptyState";
import { cx } from "@/lib/cx";

/** One related row — deliberately loose (`Record<string, unknown>` plus an
 * `id`) rather than generic over the parent record's row type: a related
 * record is a *different* entity (a property's units, in Epic 5's case),
 * with its own shape, not a projection of the record page's own row. */
export type RelatedRecordRow = { id: string } & Record<string, unknown>;

export interface RelatedRecordColumn {
  key: string;
  label: string;
  render?: (row: RelatedRecordRow) => ReactNode;
  /** Mirrors `DataTableColumn.tabularFigures`/`.align` (`components/table/
   * columns.ts`) — DESIGN.md's numeric-alignment rule is "every money
   * column, every date column, every count," which this component's own
   * date/size columns are, live-caught never applying it. */
  tabularFigures?: boolean;
  align?: "left" | "right";
}

export interface RelatedRecordsSectionProps {
  title: string;
  itemNamePlural: string;
  rows: RelatedRecordRow[];
  columns: RelatedRecordColumn[];
  getHref: (row: RelatedRecordRow) => string;
  emptyState: { body: string; action?: EmptyStateAction };
  /**
   * A header-level "add" action (Tier B fix round, 2026-08-20 — Fix 1).
   * `emptyState.action` only ever rendered inside the `rows.length === 0`
   * `EmptyState` branch — once a section had 1+ rows, whatever action a
   * caller wired up there (e.g. `PropertyRecord.tsx`'s "Add document")
   * became unreachable from anywhere in the UI, since the populated-table
   * branch below had no add affordance of its own at all, only per-row
   * `rowActions`. This prop renders as a real button next to the section's
   * own title whenever the section is populated (1+ rows) — the exact same
   * action a caller already supplies to `emptyState.action`, so both
   * branches share one action rather than inventing a second. Deliberately
   * NOT rendered for the zero-rows case: `emptyState.action`'s own ghost
   * button already covers that (the AC's own explicit "empty-state ghost
   * Add button" wording, VC-7.2-09) — showing this header button too there
   * would just be a second, redundant control on an otherwise-empty screen.
   */
  action?: EmptyStateAction;
  /**
   * A trailing, per-row action (Story 7.2) — mirrors `DataTable.tsx`'s own
   * identical `rowActions` prop exactly: hover-revealed on desktop, always
   * visible on phone (there is no hover to reveal it there), never the red
   * destructive variant directly. Generic and optional — this component
   * still renders the identical plain table it always has for every caller
   * that supplies none (Users/AlertRules-shaped related lists, were any
   * ever added), so this is a real addition to the shared shape, not a
   * one-caller special case.
   */
  rowActions?: (row: RelatedRecordRow) => ReactNode;
  /**
   * A plain, real `<a href>` link rendered in the section header, alongside
   * (never instead of) `action` — Story 7.4's own "bulk download" affordance
   * on `PropertyRecord.tsx`'s Documents section is the first real caller.
   * Deliberately a distinct prop from `action` (a button with an `onClick`,
   * gated to `rows.length > 0` and shared with `emptyState.action`, Fix 1
   * above) rather than reusing it: this is a genuine navigation to an
   * authenticated streaming download route — the same real `<a href>` shape
   * `rowActions`' own per-row "Download" link already uses (`PropertyRecord.
   * tsx`'s documents table) — not a JS click handler, and it renders
   * regardless of whether the section is empty or populated (an empty
   * property still has a real, valid — if empty — archive to download, per
   * this story's own resolved "zero documents" behaviour), unlike `action`'s
   * deliberate "populated only" gating.
   */
  headerLink?: { label: string; href: string };
}

/**
 * "Related records appear inline as compact tables" (spec judgement call 2,
 * EXPERIENCE.md § Depth rule: "never behind a tab"). Generic and reusable —
 * `RecordPage` renders zero or more of these from its own `relatedRecords`
 * prop without knowing what any of them represent, the same "no
 * route-specific branch" rule the shared table already holds itself to.
 *
 * Proven only with synthetic data (VC-2.5-19): Users has no related domain
 * entities today. Epic 5's property record (units, leases, equipment) is
 * this component's first real caller.
 */
export function RelatedRecordsSection({
  title,
  itemNamePlural,
  rows,
  columns,
  getHref,
  emptyState,
  action,
  rowActions,
  headerLink,
}: RelatedRecordsSectionProps) {
  const navigate = useNavigate();

  return (
    <section className="mt-section-gap border-t border-border-hairline pt-section-gap">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-heading font-semibold text-ink">{title}</h2>
        <div className="flex items-center gap-2">
          {/* Story 7.4: a real, authenticated download link — renders
           * whether the section is empty or populated, unlike `action`
           * below (see this prop's own doc comment on `RelatedRecordsSectionProps`). */}
          {headerLink ? (
            <a
              href={headerLink.href}
              className="inline-flex h-tap-target-min items-center rounded-md px-3 text-button text-accent hover:bg-surface-hover"
            >
              {headerLink.label}
            </a>
          ) : null}
          {/* Fix 1 (Tier B, 2026-08-20): the populated-table branch's own add
           * affordance — `emptyState.action` never renders here since `rows`
           * is non-empty below this point in that branch, so without this a
           * populated section had no add action reachable anywhere. */}
          {action && rows.length > 0 ? (
            <Button variant={action.variant ?? "ghost"} onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
        </div>
      </div>
      {/* `overflow-x-auto` below (not `overflow-hidden`): this table renders
       * one unified layout on every viewport (see the row-actions comment
       * further down), so on a narrow phone it can be wider than the
       * viewport. `overflow-hidden` here let that overflow escape the page
       * itself — live-caught dragging the app's own fixed bottom nav bar
       * off screen — instead of just clipping content silently. Scoping the
       * scroll to this container is the immediate fix; DESIGN.md's own "no
       * horizontal scroll on phone" table rule still calls for a real
       * narrow-viewport layout for this component, which is a bigger,
       * separate redesign than this fix. */}
      {rows.length === 0 ? (
        <EmptyState
          className="mt-2"
          heading={`No ${itemNamePlural} yet.`}
          body={emptyState.body}
          {...(emptyState.action ? { action: emptyState.action } : {})}
        />
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border-hairline bg-surface">
          <table className="w-full min-w-max border-collapse">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr className="h-table-header border-b border-border-hairline">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(
                      // Owner-caught, 2026-08-28: same darken/bold fix as
                      // `DataTable.tsx`'s own identical column headers.
                      "px-4 text-label font-semibold text-ink",
                      column.align === "right" ? "text-right" : "text-left",
                    )}
                  >
                    {column.label}
                  </th>
                ))}
                {rowActions ? (
                  <th scope="col" className="w-tap-target-min px-4">
                    <span className="sr-only">Row actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const href = getHref(row);
                return (
                  <tr
                    key={row.id}
                    onClick={() => navigate(href)}
                    className="h-table-row cursor-pointer border-b border-border-hairline last:border-b-0 hover:bg-surface-hover"
                  >
                    {columns.map((column, index) => {
                      const content = column.render ? column.render(row) : cellText(row[column.key]);
                      return (
                        <td
                          key={column.key}
                          className={cx(
                            "px-4 align-middle",
                            column.align === "right" ? "text-right" : "text-left",
                            column.tabularFigures ? "numeric-features text-numeric text-ink" : "text-body text-ink",
                            index === 0 ? "font-medium" : undefined,
                          )}
                        >
                          {index === 0 ? (
                            // A real `<Link>` on the identifier cell, the
                            // same shape `DataTable.tsx`'s own identifier
                            // column uses (review round: a bare `<tr
                            // onClick>` with no `<a href>` anywhere is
                            // mouse-only — untabbable, unreachable by
                            // keyboard, invisible to "open in a new tab").
                            // The row's own `onClick` above still carries
                            // the whole-row mouse/touch target; this is
                            // what makes the identical destination
                            // keyboard- and screen-reader-reachable too.
                            <Link
                              to={href}
                              className="text-ink"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {content}
                            </Link>
                          ) : (
                            content
                          )}
                        </td>
                      );
                    })}
                    {rowActions ? (
                      <td
                        className="px-4 align-middle"
                        onClick={(event) => {
                          // A click inside this cell (e.g. a real download
                          // link) must not also fire the row's own
                          // navigation `onClick` above.
                          event.stopPropagation();
                        }}
                      >
                        {/* Always visible, not hover-revealed: unlike
                         * `DataTable.tsx`'s own `rowActions` (which has a
                         * separate phone-only two-line layout to fall back
                         * to), this component renders one unified table on
                         * every viewport — a hover-only action would be
                         * unreachable on a touch device here. */}
                        <span className="flex justify-end">{rowActions(row)}</span>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function cellText(value: unknown): string {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}
